// Homepage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Homepage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  useEffect(() => {
    // Back to top functionality
    const backToTopButton = document.getElementById('backToTop');

    const handleScroll = () => {
      const header = document.querySelector('.header');
      if (window.scrollY > 50) {
        if (header) header.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
      } else {
        if (header) header.style.boxShadow = 'none';
      }

      // Show/hide back to top button
      if (backToTopButton) {
        if (window.scrollY > 300) {
          backToTopButton.classList.add('visible');
        } else {
          backToTopButton.classList.remove('visible');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Back to top click handler
    if (backToTopButton) {
      const handleBackToTop = () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      };
      backToTopButton.addEventListener('click', handleBackToTop);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        backToTopButton.removeEventListener('click', handleBackToTop);
      };
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Close mobile menu when clicking outside
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.nav')) {
        setMobileMenuOpen(false);
      }
    };

    // Close mobile menu on window resize to desktop
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileMenuOpen]);

  const handlePortalClick = (e) => {
    e.preventDefault();
    navigate('/LoginAndAuthentication');
  };

  const handleDemoClick = (e) => {
    e.preventDefault();
    navigate('/feedback');
    // You can add demo functionality here or navigate to a demo page
    console.log('Demo requested');
  };

  const handleSmoothScroll = (e) => {
    e.preventDefault();
    const href = e.target.getAttribute('href');
    if (href && href.startsWith('#')) {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  return (
    <div>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Slack-Circular-Pro', 'Helvetica Neue', Helvetica, 'Segoe UI', Tahoma, Arial, sans-serif;
          line-height: 1.6;
          color: #1d1c1d;
          background: #ffffff;
        }

        /* Header - Blue theme */
        .header {
          position: fixed;
          top: 0;
          width: 100%;
          background: #fff;
          border-bottom: 1px solid #e8e8e8;
          z-index: 1000;
          padding: 16px 0;
        }

        .nav {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          font-size: 22px;
          font-weight: 900;
          color: #ffa159;
          text-decoration: none;
        }

        .nav-menu {
          display: flex;
          list-style: none;
          gap: 32px;
        }

        .nav-menu a {
          text-decoration: none;
          color: #1d1c1d;
          font-weight: 400;
          font-size: 16px;
        }

        .nav-menu a:hover {
          color: #ffa159;
        }

        .hamburger {
          display: none;
          flex-direction: column;
          cursor: pointer;
          padding: 8px;
          z-index: 1001;
        }

        .hamburger span {
          width: 25px;
          height: 3px;
          background: #ffa159;
          margin: 3px 0;
          transition: all 0.3s ease;
          border-radius: 3px;
        }

        .hamburger.active span:nth-child(1) {
          transform: rotate(45deg) translate(8px, 8px);
        }

        .hamburger.active span:nth-child(2) {
          opacity: 0;
        }

        .hamburger.active span:nth-child(3) {
          transform: rotate(-45deg) translate(7px, -7px);
        }

        .nav-cta {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .btn {
          padding: 12px 24px;
          border-radius: 4px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          font-size: 16px;
          cursor: pointer;
        }

        .btn-primary {
          background: #ffa159;
          color: white;
        }

        .btn-primary:hover {
          background: #FF4F00;
        }

        .btn-secondary {
          background: transparent;
          color: #ffa159;
          border: 1px solid #ffa159;
        }

        .btn-secondary:hover {
          background: #FF4F00;
          color: white;
        }

        /* Hero Section - Blue theme with side-by-side layout */
        .hero {
          margin-top: 72px;
          padding: 80px 32px 80px;
          background: #ffffff;
          overflow: hidden;
        }

        .hero-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }

        .hero-content {
          text-align: left;
        }

        .hero-label {
          font-size: 14px;
          font-weight: 700;
          color: #616061;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 24px;
        }

        .hero h1 {
          font-size: 25.2px; /* Reduced from 36px by 30% */
          font-weight: 700;
          color: #ffa159;
          margin-bottom: 24px;
          line-height: 1.25;
        }


        .hero-subtitle-1 {
          font-size: 22px;
          color: #ffa159;
          margin-bottom: 10px;
          line-height: 1.5;
        }

        .hero-subtitle {
          font-size: 20px;
          color: #616061;
          margin-bottom: 40px;
          line-height: 1.5;
        }

        .hero-cta {
          display: flex;
          gap: 16px;
        }

        .hero-image {
          position: relative;
          height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-image img {
          max-width: 100%;
          height: auto;
          max-height: 600px;
        }

        .platform-stack {
          position: relative;
          width: 350px;
          height: 300px;
          perspective: 1000px;
        }

        .stack-layer {
          position: absolute;
          width: 100%;
          height: 80px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 16px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          transform-style: preserve-3d;
        }

        .layer-platform {
          bottom: 0;
          background: linear-gradient(135deg, #4FC3F7, #29B6F6);
          transform: rotateX(-5deg) translateZ(0px);
        }

        .layer-security {
          bottom: 60px;
          background: linear-gradient(135deg, #1976D2, #ffa159);
          transform: rotateX(-5deg) translateZ(20px);
        }

        .layer-scale {
          bottom: 120px;
          background: linear-gradient(135deg, #ffa159, #0D47A1);
          transform: rotateX(-5deg) translateZ(40px);
        }

        .reqsharp-icon {
          position: absolute;
          top: -20px;
          right: -20px;
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #4FC3F7, #29B6F6, #1976D2, #ffa159);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        /* Main Benefits Section - Blue theme 3-column structure */
        .benefits {
          padding: 120px 32px;
          background: white;
          clear: both;
          position: relative;
        }

        .benefits-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .benefits-title {
          font-size: 25.2px; /* Reduced from 36px by 30% */
          font-weight: 700;
          color: #ffa159;
          text-align: center;
          margin-bottom: 80px;
          line-height: 1.25;
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 80px;
        }

        .benefit {
          text-align: left;
        }

        .benefit h3 {
          font-size: 24px;
          font-weight: 700;
          color: #1d1c1d;
          margin-bottom: 24px;
          line-height: 1.25;
        }

        .benefit ul {
          list-style: none;
          margin-bottom: 32px;
        }

        .benefit li {
          font-size: 16px;
          color: #616061;
          margin-bottom: 16px;
          line-height: 1.5;
          position: relative;
          padding-left: 8px;
        }

        .benefit li:before {
          content: "•";
          color: #ffa159;
          font-weight: bold;
          position: absolute;
          left: -8px;
        }

        .benefit-link {
          color: #1976D2;
          text-decoration: none;
          font-weight: 600;
          border-bottom: 1px solid transparent;
        }

        .benefit-link:hover {
          border-bottom: 1px solid #1976D2;
        }



      /* Social Media Icons */
        .social-media {
          display: flex;
          justify-content: center;
          gap: 20px;
          margin-bottom: 24px;
        }

        .social-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          color: white;
          text-decoration: none;
          font-size: 18px;
          transition: all 0.3s ease;
          opacity: 0.8;
        }

        .social-icon:hover {
          opacity: 1;
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        @media (max-width: 768px) {
          .social-media {
            gap: 16px;
          }

          .social-icon {
            width: 36px;
            height: 36px;
            font-size: 16px;
          }
        }



        /* Integration Showcase - Blue theme app grid */
        .integrations {
          padding: 80px 32px;
          background: #f4f4f4;
        }

        .integrations-container {
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
        }

        .integrations h3 {
          font-size: 24px;
          font-weight: 700;
          color: #1d1c1d;
          margin-bottom: 40px;
        }

        .integration-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
          margin-bottom: 40px;
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
        }

        .integration-icon {
          width: 80px;
          height: 80px;
          background: white;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
          font-size: 24px;
        }

        /* Features Section */
        .features {
          padding: 120px 32px;
          background: white;
          text-align: center;
          overflow-x: hidden;
        }

        .features-container {
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }

        .features h2 {
          font-size: 25.2px; /* Reduced from 36px by 30% */
          font-weight: 700;
          color: #1d1c1d;
          margin-bottom: 24px;
          line-height: 1.25;
        }

        .features-subtitle {
          font-size: 18px;
          color: #616061;
          line-height: 1.5;
          margin-bottom: 80px;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }

        .feature-map {
          margin-top: 60px;
          text-align: center;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .feature-map img {
          max-width: 100%;
          width: 100%;
          height: auto;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.1);
          border: 1px solid #e8e8e8;
          object-fit: contain;
        }

        /* Back to Top Button */
        .back-to-top {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 50px;
          height: 50px;
          background: #ffa159;
          color: white;
          border: none;
          border-radius: 50%;
          font-size: 18px;
          cursor: pointer;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .back-to-top:hover {
          background: #FF4F00;
          transform: translateY(-2px);
        }

        .back-to-top.visible {
          opacity: 1;
          visibility: visible;
        }

        .back-to-top:before {
          content: "↑";
          display: block;
          line-height: 1;
        }

        /* Footer - Orange theme */
        .footer {
          padding: 40px 32px;
          background: #ffa159;
          color: white;
          text-align: center;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 32px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .footer-links a {
          color: white;
          text-decoration: none;
          opacity: 0.8;
        }

        .footer-links a:hover {
          opacity: 1;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hamburger {
            display: flex;
          }

          .nav-menu {
            position: fixed;
            top: 72px;
            left: 0;
            right: 0;
            background: white;
            flex-direction: column;
            padding: 20px 32px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            transform: translateY(-100%);
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
            gap: 16px;
          }

          .nav-menu.mobile-active {
            transform: translateY(0);
            opacity: 1;
            visibility: visible;
          }

          .nav-cta {
            display: none;
          }

          .hero-container {
            grid-template-columns: 1fr;
            gap: 40px;
            text-align: center;
          }

          .hero-content {
            text-align: center;
          }

          .hero h1 {
            font-size: 25.2px; /* Already reduced by 30% */
          }

          .platform-stack {
            width: 280px;
            height: 240px;
          }

          .benefits-grid {
            grid-template-columns: 1fr;
            gap: 60px;
          }

          .integration-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .features {
            padding: 80px 16px;
          }

          .features h2 {
            font-size: 19.6px; /* Reduced from 28px by 30% */
          }

          .features-subtitle {
            font-size: 16px;
            margin-bottom: 40px;
          }

          .feature-map {
            margin-top: 40px;
          }

          .feature-map img {
            border-radius: 8px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.1);
          }
        }
      `}</style>

      {/* Header */}
      <header className="header">
        <nav className="nav">
          <a href="#" className="logo">
            <img src="/logos/logo-reqsharp-light-orange.svg" alt="ReqSharp Logo" style={{height:'48px', verticalAlign:'middle'}} />
          </a>
          <button
            className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <ul className={`nav-menu ${mobileMenuOpen ? 'mobile-active' : ''}`}>
             <li> <a href="/Pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a> </li>
            <li> <a href="/feedback" onClick={() => setMobileMenuOpen(false)}>Contact Us</a> </li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1>Requirements Reimagined:</h1>

            <p className="hero-subtitle"><b>AI-Assisted Platform for Streamlined and Efficient Requirements Management</b></p>
            <p className="hero-subtitle">Transform requirements management across your organization: capture, evaluate, convert, and manage—from AI-assisted generation to RAID log management—with preloaded templates and customizable workflows</p>





            <div className="hero-cta">
              <button onClick={handleDemoClick} className="btn btn-primary">REQUEST A DEMO</button>
              <button onClick={handlePortalClick} className="btn btn-secondary">TAKE ME TO THE PORTAL</button>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/home_page_image_with_features.png" alt="Requirements Management" />
          </div>
        </div>
      </section>



{/* Yellow Section */}

      <section style={{
        background: 'white',
        padding: '0 32px',
        marginTop: '10px'
      }}>
        <div style={{
          background: '#ffa159',
          position: 'relative',
          overflow: 'hidden',
          paddingBottom: '30px',
          maxWidth: '1200px',
          margin: '0 auto',
          paddingTop: '0'
        }}>
          <svg style={{width: '100%', height: '120px', display: 'block', transform: 'translateY(-1px)'}} viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0,0 L1440,0 L1440,60 Q1080,0 720,60 Q360,120 0,60 Z" fill="white"/>
          </svg>
          <div style={{
            padding: '60px 32px'
          }}>


            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
              gap: window.innerWidth <= 768 ? '40px' : '100px',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  fontSize: window.innerWidth <= 768 ? '32px' : '52px',
                  fontWeight: '700',
                  color: '#FFFFFF',
                  lineHeight: '1.2',
                  margin: '0'
                }}>With the Right Platform, Great Things Can Happen</h2>
              </div>
              <div>
                <p style={{
                  fontSize: window.innerWidth <= 768 ? '15px' : '16px',
                  color: '#FFFFFF',
                  lineHeight: '1.8',
                  margin: '0'
                }}>ReqSharp's comprehensive feature set creates an integrated requirements management ecosystem where functional requirements, integration specifications, and project dependencies work seamlessly together. From initial capture and AI-powered generation to multi-level approvals and RAID log tracking, every feature is designed to support collaborative requirements management across your entire organization. Whether you're defining use cases, managing complex functional hierarchies, evaluating products, or converting code into requirements, ReqSharp provides the tools teams need to maintain alignment from conception through closure</p>
              </div>
            </div>
          </div>
        </div>
      </section>






      {/* Target Audience Section */}
      <section className="integrations" id="target_audience">
        <div className="integrations-container">
          <h3 style={{color:'#ffa159'}}>Who Benefits from ReqSharp</h3>
          <div className="integration-grid">
            <div style={{textAlign:'center', padding: '20px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <img src="/images/whoisthisfor/product_manager.png" alt="Product Managers" style={{width:'120px',height:'120px', marginBottom: '16px', display: 'block', margin: '0 auto 16px auto'}} />
              <div style={{marginTop:'8px', fontSize:'18px', color:'#ffa159', fontWeight:'700', marginBottom: '16px', textAlign:'center'}}>
                Product Managers
              </div>
              <ul style={{marginTop:'8px', fontSize:'14px', color:'#616061', fontWeight:'400', listStyle:'disc', paddingLeft:'20px', textAlign: 'left', lineHeight: '1.6'}}>
                <li>Streamline requirements gathering and discovery processes</li>
                <li>Leverage AI-powered requirements generation and analysis</li>
                <li>Centrally manage functional, integration, and non-functional requirements</li>
                <li>Maintain comprehensive RAID logs and risk management</li>
                <li>Facilitate stakeholder alignment and approval workflows</li>
                <li>Implement systematic requirements classification and prioritization</li>
                <li>Control and track scope changes effectively</li>
                <li>Enable seamless cross-functional team collaboration</li>
              </ul>
            </div>
            <div style={{textAlign:'center', padding: '20px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <img src="/images/whoisthisfor/enterprises.png" alt="Enterprises" style={{width:'120px',height:'120px', marginBottom: '16px', display: 'block', margin: '0 auto 16px auto'}} />
              <div style={{marginTop:'8px', fontSize:'18px', color:'#ffa159', fontWeight:'700', marginBottom: '16px', textAlign:'center'}}>
                Enterprises Across Functional Domains
              </div>
              <ul style={{marginTop:'8px', fontSize:'14px', color:'#616061', fontWeight:'400', listStyle:'disc', paddingLeft:'20px', textAlign: 'left', lineHeight: '1.6'}}>
                <li>Standardize planning processes across the entire enterprise</li>
                <li>Ensure consistency in requirements management across all projects</li>
                <li>Generate requirements from legacy code during platform migrations</li>
                <li>Provide intuitive interfaces designed for business stakeholders</li>
                <li>Establish a centralized repository for all organizational requirements</li>
                <li>Implement role-based access control and security protocols</li>
                <li>Track multi-level stakeholder approvals and sign-offs</li>
                <li>Foster collaboration across teams and departments</li>
                <li>Integrate with execution systems (Jira integration coming soon)</li>
              </ul>
            </div>
            <div style={{textAlign:'center', padding: '20px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <img src="/images/whoisthisfor/consulting_companies.png" alt="Consulting Companies" style={{width:'120px',height:'120px', marginBottom: '16px', display: 'block', margin: '0 auto 16px auto'}} />
              <div style={{marginTop:'8px', fontSize:'18px', color:'#ffa159', fontWeight:'700', marginBottom: '16px', textAlign:'center'}}>
                Consulting Companies
              </div>
              <ul style={{marginTop:'8px', fontSize:'14px', color:'#616061', fontWeight:'400', listStyle:'disc', paddingLeft:'20px', textAlign: 'left', lineHeight: '1.6'}}>
                <li>Strengthen sales cycles and increase client project win rates</li>
                <li>Accelerate requirements finalization using preloaded templates</li>
                <li>Fast-track product evaluation and assessment engagements</li>
                <li>Maintain centralized repositories for client engagement requirements</li>
                <li>Utilize AI for requirements generation, including legacy code analysis</li>
              </ul>
            </div>
            <div style={{textAlign:'center', padding: '20px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <img src="/images/whoisthisfor/product_companies.png" alt="Product Companies" style={{width:'120px',height:'120px', marginBottom: '16px', display: 'block', margin: '0 auto 16px auto'}} />
              <div style={{marginTop:'8px', fontSize:'18px', color:'#ffa159', fontWeight:'700', marginBottom: '16px', textAlign:'center'}}>Product Companies</div>
              <ul style={{marginTop:'8px', fontSize:'14px', color:'#616061', fontWeight:'400', listStyle:'disc', paddingLeft:'20px', textAlign: 'left', lineHeight: '1.6'}}>
                <li>Monitor and track requirement classifications across product portfolios</li>
                <li>Conduct comprehensive feature comparisons with competing products</li>
                <li>Improve success rates throughout the entire sales cycle</li>
                <li>Define and maintain structured product backlogs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>



      {/* Main Benefits Section */}
      <section className="benefits">
        <div className="benefits-container">
          <h2 className="benefits-title">Meet the needs of your organization with powerful requirements management tool</h2>
          <div className="benefits-grid">


            <div className="benefit">
              <h3>Automate requirements capture and get more time back for what's most important</h3>
              <ul>
                <li>Bring intelligent requirements into the flow of work. Powered by advanced AI, ReqSharp becomes more intelligent and helpful as it analyzes your codebase and generates requirements like a member of your team.</li>
                <li>Get AI assistance for specific functionality requirements. Review, Edit and Confirm these auto-generated requirements for an efficient and expedited requirements generation</li>
              </ul>
            </div>

            <div className="benefit">
              <h3>Make the best use of this extremely intuitive FUNCTIONAL platform</h3>
              <ul>
                <li>This is a very user friendly, feature rich FUNCTIONAL platform that your project team (especially business users) will love</li>
                <li>Protect sensitive requirements and project data with role-based access control</li>
                <li>Take control of your data using customizable requirement attributes and statuses</li>
              </ul>
            </div>

            <div className="benefit">
              <h3>ReqSharp offers everything you need to streamline the planning phase of your project</h3>
              <ul>
                <li>Capture, track, manage and link all your project dependencies (Risks, Actions, Issues, Decisions, Questions, Tasks, Change Requests, etc) … you can define additional dependency types and assign them to project teams for resolution</li>
                <li>Define targeted approves at project level, functional hierarchy level and individual requirements level for better ownership and accountability</li>
                <li>leverage the personal dashboard to review and act on pending approvals and other assignments (eg: RAID Log)</li>
              </ul>
            </div>



             <div className="benefit">
                <h3>Future Roadmap Items - Enhanced Features Coming Soon to Power Your Success</h3>
                <ul>
                  <li>Jira Integration for a smooth transition into the execution phase</li>
                  <li>Preloaded Requirements and Functional Hierarchy: Ongoing expansion into additional functional domains</li>
                  <li>Chat facility for instant support</li>
                  <li>Reports - Out of the box reports that you can run to get a summary view</li>
                  <li>Watchers - Watch requirements and other dependencies for updates</li>
                  <li>Email notifications - get notified anytime a requirement or a dependency is updated</li>
                  <li>Support Agile methodology - map requirements to sprints for visibility</li>
                  <li>Export data to excel for offline review</li>
                  <li>Load users in bulk - especially for large enterprises</li>
                  <li>Extend the product evaluation capability by publishing a more granular functional level maturity score (eg. OMS - Call Center Functions, WMS - Carrier Selection and Cost Optimization, Transportation - Capacity Planning etc.) </li>
                </ul>
              </div>
          </div>
        </div>
      </section>




 {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          {/* Social Media Icons */}
          <div className="social-media">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon" title="Instagram">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="social-icon" title="X (Twitter)">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon" title="LinkedIn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon" title="Facebook">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="social-icon" title="YouTube">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>

          <div className="footer-links">
            <a href="/feedback">Contact Us</a>
          </div>
          <p>&copy; 2025 ReqSharp. All rights reserved.</p>
        </div>
      </footer>






      {/* Back to Top Button */}
      <button className="back-to-top" id="backToTop" title="Back to top"></button>
    </div>
  );
};

export default Homepage;